if (!customElements.get('product-features-ilva')) {
  class ProductFeaturesIlva extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      const { signal } = this._ac;

      this.addEventListener('click', this._onClick.bind(this), { signal });

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (evt) => {
          const hotspot = evt.target.closest('.hotspot');
          if (hotspot) this._open(hotspot);
        }, { signal });
        // Deselecting returns the preview to what a visitor sees on page load —
        // the pre-opened card, when there is one — rather than to no card at all
        const toPinned = () => {
          this._close();
          this._adoptPreOpened();
        };
        this.addEventListener('shopify:block:deselect', toPinned, { signal });
        document.addEventListener('shopify:section:deselect', toPinned, { signal });
      }

      this._adoptPreOpened();
    }

    /**
     * "Open this card on page load" — desktop / mobile / both. The desktop case
     * arrives already carrying .is-active from Liquid, so the card is visible
     * with no JS-dependent flash; the mobile case cannot, because the card has
     * to be moved into the panel to be shown at all. Either way, adopt it here
     * so this._active is set and only one card can ever be open.
     */
    _adoptPreOpened() {
      const preOpened = this.querySelector('[data-open-on-load]');
      if (!preOpened) return;

      if (this._isPinned(preOpened)) {
        this._open(preOpened, { initial: true });
        return;
      }

      // Desktop-only card being viewed on mobile: undo the server-rendered state
      if (preOpened.classList.contains('is-active')) {
        preOpened.classList.remove('is-active');
        preOpened.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'false');
        preOpened.querySelector('.hotspot__card')?.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * The pre-opened card for the current viewport is pinned: while it is
     * open, an outside click or a resize does not close it (ClickUp 869f3pw9z,
     * 2026-09-23 — "we need it to always stay open"). Only the visitor closes
     * it — ✕, Escape, or opening another card — and once closed it stays
     * closed until they reopen it from its dot. Read per open rather than
     * stored, so a desktop-only card opened by hand on a phone is an ordinary
     * card there.
     */
    _isPinned(hotspot) {
      const mode = hotspot.dataset.openOnLoad;
      if (!mode) return false;
      return window.matchMedia('(min-width: 1024px)').matches
        ? mode === 'desktop' || mode === 'both'
        : mode === 'mobile' || mode === 'both';
    }

    disconnectedCallback() {
      this._ac?.abort();
      this._close();
    }

    _onClick(evt) {
      if (evt.target.closest('.hotspot__close')) {
        this._close({ restoreFocus: true });
        return;
      }

      const btn = evt.target.closest('.hotspot__btn');
      if (!btn) return;

      const hotspot = btn.closest('.hotspot');
      if (!hotspot) return;

      if (this._active === hotspot) {
        this._close();
      } else {
        this._open(hotspot);
      }
    }

    _panel() {
      return this.parentElement?.querySelector('.hotspot__mobile-panel') ?? null;
    }

    /**
     * True when a click landed on a hotspot dot. Those are outside the card but
     * must never count as "outside" for the click-away close — _onClick already
     * owns them, and closing here would fight it. Guards the target type too: a
     * click can be retargeted to a non-Element node, which has no closest().
     */
    _isHotspotTrigger(target) {
      if (!(target instanceof Element)) return false;
      const btn = target.closest('.hotspot__btn');
      return btn && this.contains(btn);
    }

    _open(hotspot, { initial = false } = {}) {
      if (this._active) this._close();
      this._active = hotspot;
      const pinned = this._isPinned(hotspot);

      hotspot.classList.add('is-active');
      hotspot.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'true');

      this._docAC = new AbortController();
      const { signal } = this._docAC;

      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this._close({ restoreFocus: true });
      }, { signal });

      // Captured per open, in the closure rather than on `this`, so it cannot
      // go stale across opens. The listener dies with the card via `signal`.
      const openWidth = window.innerWidth;
      const openDesktop = window.matchMedia('(min-width: 1024px)').matches;
      window.addEventListener('on:debounced-resize', () => {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

        // The pinned card only gives way when the card has to move between
        // .hotspot and the mobile panel. On the same side of 1024px it stays,
        // re-nudged against the new viewport edges on desktop; across it, it
        // is re-adopted, which re-reads the mode for the new viewport.
        if (pinned) {
          if (isDesktop === openDesktop) {
            if (isDesktop) this._reposition(hotspot);
            return;
          }
          this._close();
          this._adoptPreOpened();
          return;
        }

        // A height-only resize below 1024px is the phone's URL bar collapsing
        // on scroll, not a layout change — closing there is what was reported
        // (ClickUp 869f3pw9z). Crossing the 1024px breakpoint, which is the
        // only case this close exists for (the card has to move between
        // .hotspot and the mobile panel), cannot happen without a width change.
        if (!isDesktop && window.innerWidth === openWidth) return;
        this._close();
      }, { signal });

      if (!window.matchMedia('(min-width: 1024px)').matches) {
        const panel = this._panel();
        const card = hotspot.querySelector('.hotspot__card');
        if (panel && card) {
          this._mobileCardHost = hotspot;
          panel.appendChild(card);
          card.setAttribute('aria-hidden', 'false');
          panel.classList.add('is-active');
          panel.setAttribute('aria-hidden', 'false');

          // Panel is outside the component — wire close button and click-outside manually
          panel.querySelector('.hotspot__close')?.addEventListener('click', () => this._close({ restoreFocus: true }), { signal });
          // The panel IS the card on mobile, so it is the boundary — a tap on
          // the image above it closes, a tap on the panel's own padding does
          // not. Same rule as the desktop branch below; see the note there,
          // including why the pinned card has no click-away at all.
          if (!pinned) {
            document.addEventListener('click', (e) => {
              if (panel.contains(e.target)) return;
              if (this._isHotspotTrigger(e.target)) return;
              this._close();
            }, { signal });
          }

          // Neither on a card opened at page load: scrolling would yank the
          // visitor down the page before they have read anything, and trapping
          // focus would take the keyboard from the top of the document
          if (!initial) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            if (typeof trapFocus === 'function') trapFocus(panel);
          }
        }
      } else {
        const openCard = hotspot.querySelector('.hotspot__card');
        openCard?.setAttribute('aria-hidden', 'false');
        // The card itself is the boundary, not the section. The section used to
        // be, which inverted the gesture: clicking the image the card sits on —
        // the obvious way to dismiss it — did nothing, while a click far away on
        // the price closed it (ClickUp 869f3pw9z). Now anything outside the card
        // closes it, the image included.
        //
        // The dots are exempt, and that is not a nicety: this listener is added
        // while the very click that opened the card is still bubbling toward
        // document, so without the exemption a dot click would immediately close
        // what it just opened. It also keeps clicking one dot while another card
        // is open a clean swap rather than a close-then-reopen.
        //
        // The pinned card — the one opened on page load — is the exception and
        // registers no click-away: the client wants it to always stay open
        // (ClickUp 869f3pw9z, 2026-09-23). Its ✕ and Escape still close it, so
        // it is never undismissable, only never dismissed by accident.
        if (!pinned) {
          document.addEventListener('click', (e) => {
            if (openCard && openCard.contains(e.target)) return;
            if (this._isHotspotTrigger(e.target)) return;
            this._close();
          }, { signal });
        }
        // Never on a pre-opened card: trapping focus on page load steals the
        // keyboard from the top of the document and cannot be escaped by Tab
        if (!initial && typeof trapFocus === 'function') {
          // A pre-opened hotspot hides its own dot while the card is open, and
          // an element that becomes visibility:hidden while focused drops focus
          // to <body> — so a keyboard user re-opening the card would lose their
          // place. Seed the trap with the close button in that case. When the
          // dot is still visible, keep passing the wrapper: it has no tabindex,
          // so .focus() is a no-op and focus stays on the dot, as before.
          const trigger = hotspot.querySelector('.hotspot__btn');
          const triggerHidden = trigger && getComputedStyle(trigger).visibility === 'hidden';
          trapFocus(hotspot, triggerHidden ? hotspot.querySelector('.hotspot__close') : hotspot);
        }
        this._reposition(hotspot);

        // The section loads its stylesheet asynchronously (media="print" plus an
        // onload swap) whenever section.index > 2, so a card opened at page load
        // can be measured before it is styled. Re-run once everything has loaded
        // — _reposition only writes when the card still overflows, so the second
        // pass is a no-op in the normal case where the CSS won the race
        if (initial && document.readyState !== 'complete') {
          window.addEventListener('load', () => {
            if (this._active === hotspot) this._reposition(hotspot);
          }, { once: true, signal });
        }
      }
    }

    _close({ restoreFocus = false } = {}) {
      if (!this._active) return;
      const hotspot = this._active;
      this._active = null;
      this._docAC?.abort();
      this._docAC = null;

      // Read before the card is hidden: if focus is inside it (close button,
      // Escape), it has to be handed back to the dot, which the class removal
      // below makes visible again. Click-away on a non-focusable target (e.g.
      // the image) does not move focus, so check restoreFocus instead of
      // assuming focus has already moved away.
      const panel = this._panel();
      const focusWasInside = restoreFocus && (hotspot.contains(document.activeElement)
        || !!(panel && panel.contains(document.activeElement)));

      hotspot.classList.remove('is-active');
      hotspot.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'false');

      if (panel && this._mobileCardHost) {
        const card = panel.querySelector('.hotspot__card');
        if (card) {
          card.setAttribute('aria-hidden', 'true');
          this._mobileCardHost.appendChild(card);
        }
        this._mobileCardHost = null;
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      } else {
        const card = hotspot.querySelector('.hotspot__card');
        if (card) {
          card.setAttribute('aria-hidden', 'true');
          card.style.cssText = '';
        }
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(focusWasInside ? hotspot.querySelector('.hotspot__btn') : null);
      }
    }

    _reposition(hotspot) {
      // An anchored card is placed against the image by CSS, not against the
      // dot — the viewport-edge nudges below would fight that placement
      if (hotspot.className.includes('hotspot--align-')) return;

      const card = hotspot.querySelector('.hotspot__card');
      if (!card) return;

      card.style.cssText = '';
      const cr = card.getBoundingClientRect();

      if (cr.bottom > document.documentElement.clientHeight) {
        card.style.top = 'auto';
        card.style.bottom = 'calc(100% + 12px)';
      }

      if (cr.right > window.innerWidth - 20) {
        card.style.left = `calc(50% - ${cr.right - window.innerWidth + 45}px)`;
      } else if (cr.left < 20) {
        card.style.left = `calc(50% - ${cr.left - 35}px)`;
      }
    }
  }

  customElements.define('product-features-ilva', ProductFeaturesIlva);
}

if (!customElements.get('product-features-refresh')) {
  /**
   * Invisible companion element rendered outside the section's empty-state
   * guard, so it exists even when the section renders nothing. On variant
   * change it re-fetches the section for the new variant via the Section
   * Rendering API — image, hotspots and the empty state all follow the
   * selected variant without a page reload.
   */
  class ProductFeaturesRefresh extends HTMLElement {
    connectedCallback() {
      // The theme editor re-renders sections itself; a storefront-rendered
      // swap would strip design-mode block attributes and break block select
      if (window.Shopify?.designMode) return;

      this._ac = new AbortController();
      this._variantIds = new Set(
        (this.dataset.variantIds || '').split(',').filter(Boolean).map(Number)
      );
      document.addEventListener('on:variant:change', this._onVariantChange.bind(this), {
        signal: this._ac.signal
      });
    }

    disconnectedCallback() {
      this._ac?.abort();
      this._fetchAC?.abort();
    }

    async _onVariantChange(evt) {
      const variant = evt.detail?.variant;
      // Match by variant id, not product_id — product-configurator.js emits a
      // slim variant object without product_id, and this also ignores variant
      // changes from other products (e.g. quick-add modals)
      if (!variant || !this._variantIds.has(Number(variant.id))) return;

      this._fetchAC?.abort();
      this._fetchAC = new AbortController();

      const url = `${this.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.sectionId}`;

      try {
        const response = await fetch(url, { signal: this._fetchAC.signal });
        if (!response.ok) return;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrapperId = `shopify-section-${this.dataset.sectionId}`;
        const newWrapper = doc.getElementById(wrapperId);
        const wrapper = document.getElementById(wrapperId);
        // Wholesale swap: destroys and re-creates this element too, which
        // re-registers the listener via connectedCallback on the new instance
        if (newWrapper && wrapper) wrapper.innerHTML = newWrapper.innerHTML;
      } catch (error) {
        // A superseded fetch aborts here; on a network error the previous
        // variant's content simply stays in place
        if (error.name !== 'AbortError') return;
      }
    }
  }

  customElements.define('product-features-refresh', ProductFeaturesRefresh);
}
